import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

/** Types of `GET/PUT /settings/notifications` (docs/dev/api-shell.md — Notifications). */

export type Channel = 'app' | 'email' | 'push' | 'sms';
export type EventGroup =
  'deadlines' | 'deals_contacts' | 'appointments' | 'tickets_portal' | 'organization' | 'mandatory';

export interface SettingsEvent {
  key: string;
  group: EventGroup;
  name: string;
  subtext: string | null;
  leadTimePossible: boolean;
  mandatory: boolean;
  leadTimeText: string | null;
  leadTimeOptions: string[];
  app: boolean;
  email: boolean;
  push: boolean;
  sms: boolean;
  smsAvailable: boolean;
}

export interface QuietHours {
  start: string | null;
  end: string | null;
  weekend: boolean;
}

export interface Digest {
  enabled: boolean;
  time: string;
}

export interface NotificationSettings {
  events: SettingsEvent[];
  quietHours: QuietHours;
  digest: Digest;
}

export interface EventChange {
  key: string;
  app?: boolean;
  email?: boolean;
  push?: boolean;
  sms?: boolean;
  leadTimeText?: string | null;
}

export interface SettingsUpdate {
  events?: EventChange[];
  quietHours?: Partial<QuietHours>;
  digest?: Partial<Digest>;
}

export const channels: ReadonlyArray<{ key: Channel; label: string; short: string }> = [
  { key: 'app', label: 'In der App', short: 'In-App' },
  { key: 'email', label: 'E-Mail', short: 'E-Mail' },
  { key: 'push', label: 'Push', short: 'Push' },
  { key: 'sms', label: 'SMS', short: 'SMS' },
];

export const groupLabels: Record<EventGroup, string> = {
  deadlines: 'Fristen',
  deals_contacts: 'Deals und Kontakte',
  appointments: 'Termine',
  tickets_portal: 'Tickets und Mandantenportal',
  organization: 'Organisation',
  mandatory: 'Vorgegeben von der Organisation',
};

/** Group order of the API (M8: „Tickets und Mandantenportal" and „Organisation" — the latter only for admins). */
export const groupOrder: readonly EventGroup[] = [
  'deadlines',
  'deals_contacts',
  'appointments',
  'tickets_portal',
  'organization',
  'mandatory',
];

export const notificationSettingsKey = ['settings', 'notifications'] as const;

export function useNotificationSettings() {
  return useQuery({
    queryKey: notificationSettingsKey,
    queryFn: () => api<NotificationSettings>('/settings/notifications'),
    staleTime: 60_000,
  });
}

/** Applies a partial update to a cached settings object (optimistic rendering while the PUT runs). */
export function applyUpdate(current: NotificationSettings, update: SettingsUpdate): NotificationSettings {
  const events = current.events.map((event) => {
    const change = update.events?.find((entry) => entry.key === event.key);
    if (!change) return event;
    return {
      ...event,
      app: change.app ?? event.app,
      email: change.email ?? event.email,
      push: change.push ?? event.push,
      sms: change.sms ?? event.sms,
      leadTimeText: change.leadTimeText === undefined ? event.leadTimeText : change.leadTimeText,
    };
  });
  return {
    events,
    quietHours: { ...current.quietHours, ...update.quietHours },
    digest: { ...current.digest, ...update.digest },
  };
}

export function useUpdateNotificationSettings(onSaved: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (update: SettingsUpdate) =>
      api<NotificationSettings>('/settings/notifications', { method: 'PUT', body: update }),
    onMutate: async (update) => {
      await queryClient.cancelQueries({ queryKey: notificationSettingsKey });
      const previous = queryClient.getQueryData<NotificationSettings>(notificationSettingsKey);
      if (previous) queryClient.setQueryData(notificationSettingsKey, applyUpdate(previous, update));
      return { previous };
    },
    onError: (_error, _update, context) => {
      if (context?.previous) queryClient.setQueryData(notificationSettingsKey, context.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(notificationSettingsKey, data);
      onSaved();
    },
  });
}

/** "In-App und E-Mail", "Auch als Push", "Nur In-App", "Aus" — the channel summary of EM7. */
export function channelSummary(event: SettingsEvent): string {
  const on = channels.filter((channel) => event[channel.key]);
  if (on.length === 0) return 'Aus';
  if (on.length === 1 && on[0]?.key === 'app') return 'Nur In-App';
  if (event.app && event.email && on.length === 2) return 'In-App und E-Mail';
  if (event.push && on.length >= 3 && !event.sms) return 'Auch als Push';
  const labels = on.map((channel) => channel.short);
  const last = labels.pop();
  return labels.length > 0 ? `${labels.join(', ')} und ${last ?? ''}` : (last ?? 'Aus');
}

/** Groups the events in the fixed order of Shell 15; empty groups are left out. */
export function groupEvents(
  events: readonly SettingsEvent[],
): Array<{ group: EventGroup; events: SettingsEvent[] }> {
  return groupOrder
    .map((group) => ({ group, events: events.filter((event) => event.group === group) }))
    .filter((entry) => entry.events.length > 0);
}
